import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import { useLang } from "@/contexts/LanguageContext";
import { staticImg } from "@/lib/api";

export default function SavoirFairePage() {
  const { t } = useLang();
  return (
    <div data-testid="savoir-faire-page">
      {/* HERO */}
      <Section testid="savoir-hero" className="pt-32 md:pt-36">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <Reveal><Eyebrow>{t.nav.savoir}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] mt-6 whitespace-pre-line">
                  {t.savoir.hero_h1}
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 max-w-2xl text-base md:text-[17px] text-arven-offwhite/70 leading-relaxed">
                  {t.savoir.hero_sub}
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.25} className="lg:col-span-6">
              <div className="relative overflow-hidden rounded-2xl aspect-[4/5] kbd-card">
                <img src={staticImg("/api/static/generated/savoir_atelier.png")} onError={(e)=>{e.currentTarget.src=staticImg("/api/static/generated/material_bois.png")}} alt="Atelier" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* PHILOSOPHY */}
      <Section className="bg-arven-surface border-y border-white/5">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
            <div className="md:col-span-5">
              <Reveal><Eyebrow>{t.savoir.philosophy_eyebrow}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="serif text-3xl md:text-4xl lg:text-[44px] leading-tight mt-6 whitespace-pre-line">
                  {t.savoir.philosophy_h2}
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-7">
              <Reveal delay={0.2}>
                <p className="text-base md:text-[17px] leading-[1.85] text-arven-offwhite/75">
                  {t.savoir.philosophy_p}
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="savoir-stats">
                  {t.savoir.stats.map((s) => (
                    <div key={s.label} className="py-4 border-t border-white/15">
                      <p className="serif text-3xl md:text-4xl text-arven-gold">{s.value}</p>
                      <p className="mt-2 text-[11px] tracking-[0.18em] uppercase text-arven-offwhite/65">{s.label}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* PROCESS */}
      <Section testid="savoir-process">
        <Container>
          <Reveal><Eyebrow>{t.savoir.process_eyebrow}</Eyebrow></Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {t.savoir.steps.map((s, i) => (
              <Reveal key={s.t} delay={0.1 * i}>
                <div className="relative">
                  <span className="serif text-arven-gold text-3xl">0{i + 1}</span>
                  <h3 className="serif text-xl md:text-2xl mt-4">{s.t}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-arven-offwhite/65">{s.d}</p>
                  <span className="hairline mt-6 block w-full" />
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* DETAILS IMAGES */}
      <Section className="bg-arven-noir">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl aspect-[4/5]">
                <img src={staticImg("/api/static/generated/savoir_details.png")} onError={(e)=>{e.currentTarget.src=staticImg("/api/static/generated/material_metal.png")}} alt="Details craft" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative overflow-hidden rounded-2xl aspect-[4/5]">
                <img src={staticImg("/api/static/generated/savoir_plan.png")} onError={(e)=>{e.currentTarget.src=staticImg("/api/static/generated/material_pierre.png")}} alt="Plans and materials" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="bg-arven-anthracite border-t border-white/5 noise-overlay relative">
        <Container>
          <div className="max-w-3xl mx-auto text-center py-6 md:py-12">
            <Reveal>
              <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-tight">
                {t.home.cta_h2}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex justify-center">
                <Link to="/rendez-vous" className="btn-gold">{t.common.appointment} <ArrowRight size={14} /></Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </div>
  );
}
